import { useEffect, useRef } from 'react';

import { Box } from '@mui/material';

import AddApp from '@/assets/icons/AddApp';
import Button from '@/components/Buttons/Button';
import Typography from '@/components/Typography';
import { PageInformationContext } from '@/context/PageInformationContext';
import { DrawerStep, useDrawerSteps } from '@/features/Dashboard/drawer/context/DrawerStepsContext';
import { useAppForm } from '@/features/Dashboard/form/AppForm/context';
import type { SetState } from '@/types/helpers';

import { DashboardLayout } from '../../components/DashboardLayout';
import { DASHBOARD_ACTION_BUTTON_WIDTH } from '../../constants/style';
import { ButtonsContainer } from '../../drawer/components/ButtonsContainer';
import { AddAppButton } from './components/AddAppButton';

import type { DashboardStep } from '../../constants/DashboardStep';

export const EmptyDashboard = ({
  tabNames = [],
  activeTab = 0,
  onTabChange,
  setStep,
  title
}: {
  tabNames?: string[];
  activeTab?: number;
  onTabChange?: (index: number) => void;
  setStep: SetState<DashboardStep>;
  title: string;
}) => {
  const { setDrawerStep } = useDrawerSteps();

  const onClick = () => {
    setDrawerStep(DrawerStep.SelectApp);
  };

  const { setMode, mode } = useAppForm();
  const initialModeRef = useRef(mode);

  useEffect(() => {
    setMode('create');
    return () => {
      setMode(initialModeRef.current);
    };
  }, [setMode]);

  return (
    <PageInformationContext value={{ mainPageTitle: 'Dashboard' }}>
      <DashboardLayout
        activeTab={activeTab}
        renderRight={
          <ButtonsContainer>
            <Button
              color='secondary'
              size='large'
              startIcon={<AddApp fill='#F8F8F8' />}
              sx={{
                width: DASHBOARD_ACTION_BUTTON_WIDTH
              }}
              variant='contained'
              onClick={() => {
                setDrawerStep(DrawerStep.SelectApp);
              }}
            >
              Add app
            </Button>
          </ButtonsContainer>
        }
        tabNames={tabNames}
        title={title}
        onTabChange={onTabChange}
      >
        <Box
          sx={{
            mt: 2,
            marginBottom: 1,
            gap: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Typography
            fontWeight='bold'
            size={24}
            textAlign='center'
          >
            Start Building Your Dashboard
          </Typography>
          <Typography
            size={20}
            sx={{
              mb: 1
            }}
            textAlign='center'
          >
            Organize your perfect workspace by adding apps
          </Typography>
          <Button
            size='large'
            variant='contained'
            onClick={onClick}
          >
            <AddApp
              fill='#F8F8F8'
              sx={{
                width: '24px',
                height: '24px'
              }}
            />
            <Box
              sx={{
                marginLeft: '8px'
              }}
            >
              Add Your First App
            </Box>
          </Button>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gap: {
              xs: 1,
              md: 3
            },
            gridTemplateColumns: {
              xs: '1fr',
              md: 'repeat(2, 1fr)'
            },
            width: '100%'
          }}
        >
          <AddAppButton onClick={onClick} />
          <AddAppButton onClick={onClick} />
        </Box>
      </DashboardLayout>
    </PageInformationContext>
  );
};
